/**
 * Form Validation Utilities
 * Client-side validation helpers
 */

/**
 * Email validation
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Required field validation
 * @param {string} value - Value to check
 * @returns {boolean} True if not empty
 */
export function isRequired(value) {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return value !== null && value !== undefined
}

/**
 * Minimum length validation
 * @param {string} value - Value to check
 * @param {number} minLength - Minimum length
 * @returns {boolean} True if valid
 */
export function minLength(value, minLength) {
  return value && value.length >= minLength
}

/**
 * Maximum length validation
 * @param {string} value - Value to check
 * @param {number} maxLength - Maximum length
 * @returns {boolean} True if valid
 */
export function maxLength(value, maxLength) {
  return !value || value.length <= maxLength
}

/**
 * Password strength validation
 * @param {string} password - Password to validate
 * @returns {object} Validation result with isValid and errors
 */
export function validatePassword(password) {
  const errors = []
  
  if (!password) {
    errors.push('Password is required')
  } else {
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters')
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter')
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter')
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number')
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push('Password must contain at least one special character')
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validate form fields
 * @param {object} values - Form values
 * @param {object} rules - Validation rules
 * @returns {object} Validation result with isValid and errors
 */
export function validateForm(values, rules) {
  const errors = {}
  
  Object.keys(rules).forEach((field) => {
    const fieldRules = rules[field]
    const value = values[field]
    
    // Required validation
    if (fieldRules.required && !isRequired(value)) {
      errors[field] = fieldRules.requiredMessage || `${field} is required`
      return
    }
    
    // Skip other validations if field is empty and not required
    if (!isRequired(value)) {
      return
    }
    
    // Email validation
    if (fieldRules.email && !isValidEmail(value)) {
      errors[field] = fieldRules.emailMessage || 'Invalid email address'
      return
    }
    
    // Min length validation
    if (fieldRules.minLength && !minLength(value, fieldRules.minLength)) {
      errors[field] = fieldRules.minLengthMessage || `Must be at least ${fieldRules.minLength} characters`
      return
    }
    
    // Max length validation
    if (fieldRules.maxLength && !maxLength(value, fieldRules.maxLength)) {
      errors[field] = fieldRules.maxLengthMessage || `Must be no more than ${fieldRules.maxLength} characters`
      return
    }
    
    // Custom validation
    if (fieldRules.custom && typeof fieldRules.custom === 'function') {
      const customError = fieldRules.custom(value, values)
      if (customError) {
        errors[field] = customError
        return
      }
    }
  })
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Get first error message from validation result
 * @param {object} errors - Validation errors object
 * @returns {string|null} First error message or null
 */
export function getFirstError(errors) {
  const firstKey = Object.keys(errors)[0]
  return firstKey ? errors[firstKey] : null
}
